import React, { useState } from 'react';
import { Box, CheckBox } from 'grommet';
import { StepFormWrapper } from '../../molecules/form-tab-presenter/form-tabStyles';
import {
  OrderFlowTabs,
  setLegalConsentTwo,
  setDiscount,
  OrderFlow,
  setProducts
} from '../../../redux/orderFlow/orderFlowSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { NextBackButtons } from '../../atoms/nextback/next-back-buttons';
import {
  createMainContract,
  createExtraContract
} from '../../../redux/api-v2/ContractsApi';
import ReCAPTCHA from 'react-google-recaptcha';
import OrderSummaryCalculation from '../../molecules/order-summary/order-summary-calculation';
import { ConditionsAndProduct } from '../../molecules/legal-links/conditions-and-product';
import { RequiredCheckWrapper } from '../../atoms/check-box/check-box';
import { useTranslation } from 'react-i18next';
import { navigateToNextTab } from '../../../utils/navigation-helpers';
import {
  ContractMainRequest,
  CurrencyCode,
  ContractExtraRequest
} from '@oncampusabroad/api';
import { createDateFromString } from '../../../utils/date-formatter';
import { RootState } from '../../../redux/store';
import { SessionManager } from '../../../utils/session-manager';

const OrderSummary = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const orderDetails = useAppSelector(state => state.orderFlow);
  const relatedOrderId = useAppSelector(
    state => state.orders.order.response.id
  );
  const legalConsentTwo = useAppSelector(
    state => state.orderFlow.legalConsentTwo
  );

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token') || undefined;
  const [recaptcha, setRecaptcha] = useState<boolean>(false);
  const { currentTab, flow, ...orderDetailsWithoutHandlers } = orderDetails;
  const currentFlow = useAppSelector(state => state.orderFlow.flow);

  const hasProducts =
    currentFlow === OrderFlow.Flow2
      ? orderDetails.orderedProducts && orderDetails.orderedProducts.length > 0
      : true;

  const apiProductsResponse = useAppSelector(
    (state: RootState) => state.products.products.response
  );
  const apiProducts = Array.isArray(apiProductsResponse)
    ? apiProductsResponse
    : [];
  const getProductObjectIds = (products: string[]) => {
    return products
      .map(productId => {
        const product = apiProducts.find(p => p.productId === productId);
        return product?.id;
      })
      .filter((id): id is string => id !== undefined);
  };

  const createContractRequest = (): ContractMainRequest => {
    const productsToUse = [...orderDetails.orderedProducts];

    if (
      currentFlow === OrderFlow.Flow1 &&
      !productsToUse.includes('MAIN2024')
    ) {
      productsToUse.push('MAIN2024');
    }

    const productObjectIds = getProductObjectIds(productsToUse);

    return {
      firstName: orderDetails.firstName,
      lastName: orderDetails.lastName,
      departureDate: createDateFromString(orderDetails.departureDate),
      returnDate: createDateFromString(orderDetails.returnDate),
      homeCountryCode: orderDetails.homeCountryCode,
      destinationCountryCode: orderDetails.destinationCountryCode,
      documentType: orderDetails.documentType,
      idCard: orderDetails.idCard,
      birthday: createDateFromString(orderDetails.birthday),
      phoneNumber: orderDetails.phoneNumber,
      email: orderDetails.email,
      orderedProducts: productObjectIds,
      discountCode: orderDetails.discountCode || '',
      currency: orderDetails.currency || CurrencyCode.Eur,
      withEHIC: orderDetails.withEHIC,
      newsConsent: orderDetails.newsConsent,
      legalConsentOne: orderDetails.legalConsentOne,
      legalConsentTwo: orderDetails.legalConsentTwo,
      flowId: SessionManager.getUserId() || ''
    };
  };

  const createExtraContractRequest = (): ContractExtraRequest => {
    const productObjectIds = getProductObjectIds(orderDetails.orderedProducts);

    return {
      relatedOrderId: relatedOrderId || '',
      orderedProducts: productObjectIds,
      currency: orderDetails.currency || CurrencyCode.Eur,
      withEHIC: orderDetails.withEHIC,
      newsConsent: orderDetails.newsConsent,
      legalConsentOne: orderDetails.legalConsentOne,
      legalConsentTwo: orderDetails.legalConsentTwo,
      flowId: SessionManager.getUserId() || ''
    };
  };

  const [privacyCheck, setPrivacyCheck] = useState<boolean>(
    orderDetails.legalConsentTwo
  );
  const flow2products = orderDetails.flow2Products;
  //@ts-ignore
  const Environment = window.OPSEEKER_CONFIG.ENVIRONMENT;
  const handleContinue = () => {
    if (
      privacyCheck &&
      (recaptcha || Environment === 'staging' || Environment === 'qa') &&
      currentFlow === OrderFlow.Flow1
    ) {
      dispatch(createMainContract(createContractRequest()));
    } else if (
      privacyCheck &&
      (recaptcha || Environment === 'staging' || Environment === 'qa') &&
      currentFlow === OrderFlow.Flow2
    ) {
      dispatch(
        createExtraContract({
          request: createExtraContractRequest(),
          token
        })
      );
    }
  };
  const handleBack = () => {
    if (currentFlow === OrderFlow.Flow2) {
      flow2products && dispatch(setProducts({ products: flow2products }));
    }
    setRecaptcha(false);
    setPrivacyCheck(false);
    dispatch(
      setDiscount({
        discountCode: '',
        discount: false
      })
    );
    dispatch(setLegalConsentTwo({ legalConsentTwo: false }));
    navigateToNextTab(dispatch, OrderFlowTabs.ExtendedInsurance);
  };
  return (
    <Box align='center'>
      <StepFormWrapper $summary>
        <h1>{t('your-request')}:</h1>
        <OrderSummaryCalculation />
        <RequiredCheckWrapper>
          <CheckBox
            data-e2eid='check_box_privacy_check'
            label={''}
            checked={privacyCheck}
            onChange={event => {
              const newCheck = event.target.checked;
              setPrivacyCheck(newCheck);
              dispatch(setLegalConsentTwo({ legalConsentTwo: newCheck }));
            }}
          />
          <ConditionsAndProduct />
        </RequiredCheckWrapper>

        {legalConsentTwo &&
          Environment !== 'staging' &&
          Environment !== 'qa' && (
            <ReCAPTCHA
              style={{ alignSelf: 'center' }}
              //@ts-ignore
              sitekey={window.OPSEEKER_CONFIG.RECAPTCHA_SITE_KEY}
              onChange={() => setRecaptcha(true)}
            />
          )}
        <NextBackButtons
          next={{
            f: handleContinue,
            label: 'NEXT',
            disabled: !privacyCheck || !hasProducts
          }}
          back={{ f: handleBack, label: 'BACK' }}
        />
      </StepFormWrapper>
    </Box>
  );
};

export default OrderSummary;
